import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareModal from '@/components/base/ShareModal';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsArticles } from '@/mocks/newsArticles';
import { formatDateCn } from '@/utils/format';

export default function NewsDetail() {
  const { id } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const article = newsArticles.find((item) => String(item.id) === id);

  usePageMeta({
    title: article ? `${article.title} - 禁止的爱善良的小姨子动态` : '资讯详情 - 禁止的爱善良的小姨子专题',
    description: article ? article.summary : '禁止的爱善良的小姨子最新动态详情。',
    keywords: article
      ? `${article.title},禁止的爱善良的小姨子动态,${article.tags.join(',')}`
      : '禁止的爱善良的小姨子,影视动态',
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-8">
          <EmptyState
            title="没有找到这篇动态"
            description="该内容可能已归档，返回动态分类页查看最新资讯。"
            actionLabel="返回动态列表"
            actionTo="/category/news"
          />
        </div>
      </SiteLayout>
    );
  }

  const related = newsArticles.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-5 md:px-8">
          <Breadcrumb
            items={[
              { label: '动态', to: '/category/news' },
              { label: article.category },
            ]}
          />
        </div>
      </div>

      <article className="mx-auto w-full max-w-[920px] px-4 pb-24 pt-8 md:px-8 md:pt-10 lg:pb-10">
        <header>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-sm bg-primary-600 px-2 py-1 text-background-50">{article.category}</span>
            <span className="text-foreground-400">{formatDateCn(article.date)}</span>
          </div>
          <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-[34px]">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{article.summary}</p>
          <div className="mt-5 flex items-center justify-between border-y border-background-200 py-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-400">
              <span>禁止的爱善良的小姨子 · 专题编辑部</span>
              <span className="h-2.5 w-[1px] bg-background-300" />
              <span>阅读约 {article.paragraphs.join('').length > 600 ? 4 : 3} 分钟</span>
            </div>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 sm:flex"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-share-line" />
              </span>
              分享
            </button>
          </div>
        </header>

        <div className="mt-6 h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[400px]">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 禁止的爱善良的小姨子动态`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5 text-[15px] leading-[2] text-foreground-700">
          {article.paragraphs.map((paragraph) => (
            <p key={paragraph.slice(0, 14)}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
          <span className="text-xs text-foreground-400">相关标签</span>
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-6 flex flex-col items-center gap-3 rounded-lg bg-background-100 p-5 text-center">
          <p className="text-sm text-foreground-600">觉得这篇内容有用？分享给同样在追这部剧的朋友</p>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-share-forward-line" />
            </span>
            分享这篇动态
          </button>
        </div>

        <section className="mt-10 border-t border-background-200 pt-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-lg text-foreground-900">相关文章推荐</h2>
            <Link
              to="/category/news"
              className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              返回动态列表
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
          <div className="mt-4 divide-y divide-background-200 border-y border-background-200">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/news/${item.id}`}
                className="group flex items-center gap-4 py-4"
              >
                <div className="h-[64px] w-[92px] shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={item.image}
                    alt={item.title}
                    title={`${item.title} - 禁止的爱善良的小姨子动态`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] text-primary-500">{item.category}</p>
                  <h3 className="mt-1 line-clamp-2 font-heading text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                </div>
                <span className="hidden shrink-0 text-[11px] text-foreground-400 sm:block">{item.date}</span>
              </Link>
            ))}
          </div>
        </section>
      </article>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm text-background-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-line" />
          </span>
          分享这篇动态
        </button>
        <Link
          to="/category/news"
          className="flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700"
        >
          动态列表
        </Link>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </SiteLayout>
  );
}